/*
  <casalta-carousel> — the paged image component.

  Lives in its own asset rather than a section's {% javascript %} block.
  Shopify compiles section JS into one bundle but only RUNS the sections
  listed in the script tag's data-sections attribute — the sections actually
  rendered on that page. A second section reusing this element would find the
  class present in the file and never executed. Linking the asset directly
  makes it load wherever it is used.

  Used by:
    sections/casalta-carousel.liquid              home, per-slide label and title
    sections/casalta-project.liquid               interiors gallery, images only
    sections/casalta-interiors-slideshow.liquid   one-photo steps (data-step)

  The label and title elements are optional — everything below null-checks —
  which is what lets a gallery feed it images and nothing else.

  COLLAPSE. An element carrying data-collapse="<px>" pages by SLIDE above that
  width and by individual IMAGE below it — a three-up spread becomes three
  slides on a phone, where three across would be unreadable. Opt-in by
  attribute, so the home carousel is unaffected and keeps its diptychs.

  The unit of paging is whatever is in this.slides, so collapsing is only a
  matter of re-reading that list and letting CSS make the frames the track's
  children (display: contents on the slide). The transform, the wrap and the
  counter all follow from it and need no special cases.

  STEP. An element carrying data-step="slide" shows SEVERAL slides at once and
  moves along by ONE, sliding — the interiors slideshow, three photographs
  across. Every other carousel pages by a whole view and cuts.

  The move is measured, not multiplied: the track is shifted by the next
  slide's own offset in pixels, so the gap between photos is included and the
  number in view can change at a breakpoint without the script knowing it.
  Re-measured when the view resizes, with the transition suppressed so a
  resize does not animate.

  LOOP. The strip is endless: after the last photograph comes the first
  again, sliding on, never rewinding. The whole set is cloned once before
  and once after the real slides (aria-hidden, stripped of ids and the
  editor's attributes), so there is always more strip in either direction.
  Moves happen as normal into the clones; when a move finishes outside the
  real set, the strip jumps instantly by one set's width to the identical
  real position — the layout is periodic, so nothing on screen changes. The
  counter and caption are read from whichever slide leads, clone or not.

  STOPS. The strip only ever comes to rest where the right edge of the view
  meets the end of a slide. With every slide the same width that is every
  position. A landscape slide (two places wide) would otherwise be left half
  in view at the right edge, where its left half reads as a separate
  portrait photograph; those positions are skipped, so that one click moves
  on until the whole photograph is in. The first photograph of each set is
  always a stop, so the strip can start on it. In step mode this.current
  indexes the stops, not the slides.

  DRAG. In step mode the strip can also be dragged, by mouse or finger: it
  follows the pointer and, on release, settles on the nearest stop — a short
  flick of more than 40px still moves one place. It loops like the arrows.
  A drag only starts once the
  pointer has moved sideways more than it has vertically, so a phone can
  still scroll the page up and down over the strip (with touch-action: pan-y
  in the CSS). The click that ends a drag is swallowed, so letting go over a
  click zone does not step a second time.
*/

class CasaltaCarousel extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-track]');
    this.groups = Array.from(this.querySelectorAll('[data-slide]'));
    this.frames = Array.from(this.querySelectorAll('[data-frame]'));
    this.slides = this.groups;
    this.current = 0;

    if (!this.track || this.groups.length < 2) return;

    /* NAV OUTSIDE THE ELEMENT. data-nav takes a selector for a container of
       controls that sit elsewhere on the page — the product page puts
       < 1 / 6 > on the same line as the product title, and that title lives in
       the info column, not in the carousel. Optional: without the attribute
       only this element is searched, so the home carousel and the interiors
       gallery are unchanged.

       Safe to resolve here because this file is loaded with defer, so the
       element is upgraded after the whole document is parsed — the target
       exists whichever order the two appear in the DOM. */
    const navSelector = this.getAttribute('data-nav');
    this.navRoot = navSelector ? document.querySelector(navSelector) : null;

    this.currentLabel = this.findOne('[data-current]');
    this.totalLabel = this.findOne('[data-total]');

    /* Per-slide caption outputs are always inside the element — they belong to
       the carousel, not to a nav bar somewhere else. */
    this.titleOut = this.querySelector('[data-title-out]');
    this.metaOut = this.querySelector('[data-meta-out]');

    /* Captions that contain LINKS cannot be swapped as text, so every slide's
       titles are rendered up front and the current one is revealed. Building
       anchors from a data attribute would mean writing innerHTML; this way
       they are real anchors, crawlable and working with JS off.

       Indexed by SLIDE. Under collapse the paging unit is a frame, so render()
       maps the frame back to its slide for the slot, and writes which picture
       of that slide it is to data-part (0 = first) — the interiors carousel
       shows the matching half of the caption with it. */
    this.titleSlots = Array.from(this.querySelectorAll('[data-title-slot]'));
    this.labelOut = this.querySelector('[data-label-out]');

    // Arrows and click zones both carry these attributes
    this.findAll('[data-prev]').forEach((el) =>
      el.addEventListener('click', () => this.go(this.current - 1))
    );
    this.findAll('[data-next]').forEach((el) =>
      el.addEventListener('click', () => this.go(this.current + 1))
    );

    /* STEP — see the header. Measured in pixels, so it re-renders whenever
       the view changes size. */
    if (this.getAttribute('data-step') === 'slide') {
      this.stepMode = true;
      this.viewport = this.track.parentElement;
      this.initLoop();
      if ('ResizeObserver' in window) {
        new ResizeObserver(() => this.render(true, true)).observe(this.viewport);
      } else {
        window.addEventListener('resize', () => this.render(true, true));
      }
      this.render(true);
      this.initDrag();
      return;
    }

    /* ACTIVE-BELOW. An element carrying data-active-below="<px>" is a carousel
       only under that width and an ordinary block above it — the product page
       is a vertical stack beside a thumbnail rail on desktop, and a paged
       slideshow on a phone. Opt-in by attribute, so the home carousel and the
       interiors gallery are unaffected.

       Above the breakpoint the transform is CLEARED rather than left at
       translateX(-0%): someone who pages to image 3 and then widens the window
       would otherwise keep -200% on a track that is now a vertical stack, and
       every image would sit off screen with no way to bring it back.

       Not combined with data-collapse. Collapse changes the paging UNIT; this
       switches paging off entirely. No element uses both. */
    const activeBelow = parseInt(this.getAttribute('data-active-below') || '0', 10);
    if (activeBelow > 0) {
      this.activeMq = window.matchMedia('(max-width: ' + (activeBelow - 1) + 'px)');
      const syncActive = () => this.setActive(this.activeMq.matches);
      if (this.activeMq.addEventListener) {
        this.activeMq.addEventListener('change', syncActive);
      } else {
        this.activeMq.addListener(syncActive);
      }
      syncActive();
      return;
    }

    const collapseAt = parseInt(this.getAttribute('data-collapse') || '0', 10);
    if (collapseAt > 0 && this.frames.length > 1) {
      this.narrow = window.matchMedia('(max-width: ' + (collapseAt - 1) + 'px)');
      const sync = () => this.setUnit(this.narrow.matches);
      if (this.narrow.addEventListener) {
        this.narrow.addEventListener('change', sync);
      } else {
        this.narrow.addListener(sync);
      }
      sync();
      return;
    }

    this.render();
  }

  /* Switching unit changes how many pages there are, so the position has to be
     translated rather than kept: image 4 of a collapsed gallery is not slide 4.
     Map through the DOM — find the frame we are looking at, then the slide that
     contains it, or vice versa — so crossing the breakpoint leaves the same
     photograph on screen. */
  setUnit(collapsed) {
    const from = this.slides ? this.slides[this.current] : null;
    this.classList.toggle('is-collapsed', collapsed);
    this.slides = collapsed ? this.frames : this.groups;

    let next = 0;
    if (from) {
      if (collapsed) {
        const firstFrame = from.querySelector('[data-frame]') || from;
        next = Math.max(0, this.frames.indexOf(firstFrame));
      } else {
        const group = from.closest('[data-slide]');
        next = Math.max(0, this.groups.indexOf(group));
      }
    }
    this.current = next;
    this.render();
  }

  /* This element, plus the external nav container if data-nav named one.
     [data-slide] and [data-frame] are deliberately NOT looked up this way —
     the pages themselves always live inside the carousel. */
  findAll(selector) {
    const found = Array.from(this.querySelectorAll(selector));
    if (this.navRoot) {
      found.push(...Array.from(this.navRoot.querySelectorAll(selector)));
    }
    return found;
  }

  findOne(selector) {
    return (
      this.querySelector(selector) ||
      (this.navRoot ? this.navRoot.querySelector(selector) : null)
    );
  }

  /* Paging switched off above the breakpoint. The class is what CSS keys the
     desktop stack off; clearing the transform is what stops a mid-slideshow
     position stranding the images when the window widens. */
  setActive(active) {
    this.active = active;
    this.classList.toggle('is-inactive', !active);

    if (!active) {
      this.current = 0;
      this.track.style.transform = '';
      if (this.currentLabel) this.currentLabel.textContent = '1';
      return;
    }

    this.render();
  }

  go(index) {
    /* undefined for every carousel without data-active-below, so this is inert
       for the home carousel and the interiors gallery. */
    if (this.active === false) return;

    // Step mode loops through its clones instead — see LOOP in the header.
    if (this.stepMode) {
      const count = this.stops().length;
      this.current = Math.max(0, Math.min(count - 1, index));
      this.render();
      return;
    }

    // Wrap at both ends
    const count = this.slides.length;
    this.current = ((index % count) + count) % count;
    this.render();
  }

  /* Step mode — see LOOP in the header. */
  initLoop() {
    const real = this.slides;
    this.realCount = real.length;
    const copy = (el) => {
      const c = el.cloneNode(true);
      c.setAttribute('aria-hidden', 'true');
      c.setAttribute('data-clone', '');
      Array.from(c.attributes).forEach((a) => {
        if (a.name.indexOf('data-shopify') === 0) c.removeAttribute(a.name);
      });
      c.querySelectorAll('[id]').forEach((n) => n.removeAttribute('id'));
      c.querySelectorAll('img').forEach((img) => img.setAttribute('loading', 'lazy'));
      return c;
    };
    const before = real.map(copy);
    const after = real.map(copy);
    before.forEach((c) => this.track.insertBefore(c, real[0]));
    after.forEach((c) => this.track.appendChild(c));
    this.slides = before.concat(real, after);
    this.lead = this.realCount;

    this.track.addEventListener('transitionend', (e) => {
      if (e.target === this.track && e.propertyName === 'transform') this.normalize();
    });
  }

  /* After a move: if the leading slide is a clone, jump to the real one in
     the same place on screen. Instant, so it is invisible. */
  normalize() {
    const n = this.realCount;
    const stops = this.stops();
    const pos = stops[this.current];
    if (pos >= n && pos < 2 * n) return;
    const target = n + ((((pos - n) % n) + n) % n);
    const k = stops.indexOf(target);
    if (k === -1) return;
    this.current = k;
    this.render(true);
  }

  /* Step mode: the pixel offset of stop i. */
  offsetFor(i, stops) {
    return this.slides[stops[i]].offsetLeft - this.slides[0].offsetLeft;
  }

  /* Step mode only — see DRAG in the header. */
  initDrag() {
    const vp = this.viewport;
    let active = false;
    let dragging = false;
    let pointer = null;
    let startX = 0;
    let startY = 0;
    let base = 0;
    let dx = 0;
    let stops = [];

    vp.addEventListener('pointerdown', (e) => {
      if (e.button !== 0) return;
      active = true;
      dragging = false;
      this.dragged = false;
      pointer = e.pointerId;
      startX = e.clientX;
      startY = e.clientY;
      dx = 0;
      stops = this.stops();
      base = this.offsetFor(Math.min(this.current, stops.length - 1), stops);
      // Stops the browser starting its own image drag or a text selection.
      if (e.pointerType === 'mouse') e.preventDefault();
    });

    vp.addEventListener('pointermove', (e) => {
      if (!active || e.pointerId !== pointer) return;
      dx = e.clientX - startX;
      if (!dragging) {
        if (Math.abs(dx) < 6) return;
        if (Math.abs(e.clientY - startY) > Math.abs(dx)) {
          active = false; // a vertical scroll, not ours
          return;
        }
        dragging = true;
        this.dragged = true;
        vp.setPointerCapture(pointer);
        this.track.classList.add('is-dragging');
      }
      // Two sets of clones either side, so a drag never reaches an end;
      // clamped only as a guard.
      const max = this.offsetFor(stops.length - 1, stops);
      const x = Math.max(0, Math.min(max, base - dx));
      this.track.style.transform = 'translateX(' + -x + 'px)';
    });

    const end = (e) => {
      if (!active || e.pointerId !== pointer) return;
      active = false;
      if (!dragging) return;
      this.track.classList.remove('is-dragging');

      const target = base - dx;
      let best = 0;
      let bestDistance = Infinity;
      stops.forEach((_, i) => {
        const d = Math.abs(this.offsetFor(i, stops) - target);
        if (d < bestDistance) {
          bestDistance = d;
          best = i;
        }
      });
      if (best === this.current && Math.abs(dx) > 40) {
        best = this.current + (dx < 0 ? 1 : -1);
      }
      this.current = Math.max(0, Math.min(stops.length - 1, best));
      this.render();
    };
    vp.addEventListener('pointerup', end);
    vp.addEventListener('pointercancel', end);

    // Capture phase, so it runs before the click zones' own handlers.
    vp.addEventListener(
      'click',
      (e) => {
        if (!this.dragged) return;
        this.dragged = false;
        e.preventDefault();
        e.stopPropagation();
      },
      true
    );
  }

  /* Step mode: the slides the strip may rest on — see STOPS above. The last
     position always counts, since the strip ends there, and so does the first
     photograph of each set, so the strip can start on photograph 1. */
  stops() {
    const origin = this.slides[0].offsetLeft;
    const view = this.viewport.clientWidth;
    const rights = this.slides.map((el) => el.offsetLeft + el.offsetWidth - origin);
    const last = this.lastStep();
    const out = [];
    for (let i = 0; i <= last; i++) {
      const edge = this.slides[i].offsetLeft - origin + view;
      if (
        i === last ||
        i % this.realCount === 0 ||
        rights.some((r) => Math.abs(r - edge) <= 2)
      ) {
        out.push(i);
      }
    }
    return out;
  }

  /* Step mode: the first slide from which everything left fits in the view. */
  lastStep() {
    const origin = this.slides[0].offsetLeft;
    const last = this.slides[this.slides.length - 1];
    const end = last.offsetLeft + last.offsetWidth - origin;
    const view = this.viewport.clientWidth;
    for (let i = 0; i < this.slides.length; i++) {
      if (end - (this.slides[i].offsetLeft - origin) <= view + 1) return i;
    }
    return this.slides.length - 1;
  }

  render(instant, keepLead) {
    const slide = this.slides[this.current];

    if (this.stepMode) {
      const stops = this.stops();

      /* The source of truth is the leading SLIDE, not the stop number: when
         the view resizes the stops change, and the same photograph should
         stay in front. On first render this.lead is photograph 1. */
      if (keepLead || this.leadPending !== false) {
        let k = stops.indexOf(this.lead);
        if (k === -1) k = stops.findIndex((st) => st > this.lead);
        if (k === -1) k = stops.length - 1;
        this.current = k;
        this.leadPending = false;
      }
      this.current = Math.max(0, Math.min(this.current, stops.length - 1));
      this.lead = stops[this.current];
      const lead = this.slides[this.lead];
      const x = lead.offsetLeft - this.slides[0].offsetLeft;

      /* Instant: the class switches the transition off, the forced reflow
         makes the browser apply the new position WHILE it is off, and only
         then is it switched back on. Removing the class a frame later is not
         enough — the browser can apply both in one go and animate anyway,
         which for the loop's jump would slide the whole strip across. */
      if (instant) {
        this.track.classList.add('is-instant');
        this.track.style.transform = 'translateX(-' + x + 'px)';
        void this.track.offsetWidth;
        this.track.classList.remove('is-instant');
      } else {
        this.track.style.transform = 'translateX(-' + x + 'px)';
      }

      /* The count is of the stops within ONE set — photographs, when they are
         all one width — and the number shown is where the leading slide sits
         among them, clone or not. The caption names the leading slide. */
      const n = this.realCount;
      const own = stops.filter((st) => st >= n && st < 2 * n);
      const home = n + ((((this.lead - n) % n) + n) % n);
      if (this.currentLabel) this.currentLabel.textContent = String(own.indexOf(home) + 1);
      if (this.totalLabel) this.totalLabel.textContent = String(own.length);
      if (this.titleOut) {
        this.titleOut.textContent = lead.getAttribute('data-title') || '';
      }
      if (this.metaOut) {
        this.metaOut.textContent = lead.getAttribute('data-meta') || '';
      }

      // No transition (reduced motion) means no transitionend to wait for.
      if (!instant && parseFloat(getComputedStyle(this.track).transitionDuration) === 0) {
        this.normalize();
      }
      return;
    }

    this.track.style.transform = 'translateX(-' + this.current * 100 + '%)';

    if (this.currentLabel) {
      this.currentLabel.textContent = String(this.current + 1);
    }

    /* The total changes with the unit, so it is written here rather than left
       as the Liquid-rendered slide count. */
    if (this.totalLabel) {
      this.totalLabel.textContent = String(this.slides.length);
    }

    if (this.titleSlots.length) {
      let slotIndex = this.current;
      let part = 0;
      if (this.classList.contains('is-collapsed')) {
        const group = slide.closest('[data-slide]');
        slotIndex = this.groups.indexOf(group);
        part = Math.max(0, Array.from(group.querySelectorAll('[data-frame]')).indexOf(slide));
      }
      this.titleSlots.forEach((el, i) => {
        el.hidden = i !== slotIndex;
      });
      this.setAttribute('data-part', String(part));
    } else if (this.titleOut) {
      this.titleOut.textContent = slide.getAttribute('data-title') || '';
    }

    if (this.labelOut) {
      const label = slide.getAttribute('data-label') || '';
      const href = slide.getAttribute('data-label-href') || '';
      this.labelOut.textContent = label;
      if (href) {
        this.labelOut.setAttribute('href', href);
      } else {
        this.labelOut.removeAttribute('href');
      }
    }
  }
}

if (!customElements.get('casalta-carousel')) {
  customElements.define('casalta-carousel', CasaltaCarousel);
}
